import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Plus, Clock, Trash2, Loader2, TrendingUp, Users } from "lucide-react";
import { toast } from "sonner";
import { fmtEUR, fmtDate } from "../lib/format";

export default function Timesheet() {
  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [team, setTeam] = useState([]);
  const [weekSummary, setWeekSummary] = useState(null);
  const [filterProject, setFilterProject] = useState("");
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [e, p, t, w] = await Promise.all([
        api.get("/time-entries"),
        api.get("/projects"),
        api.get("/team").catch(() => ({ data: [] })),
        api.get("/dashboard/hours-this-week").catch(() => ({ data: null })),
      ]);
      setEntries(e.data); setProjects(p.data); setTeam(t.data); setWeekSummary(w.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return filterProject ? entries.filter((e) => e.project_id === filterProject) : entries;
  }, [entries, filterProject]);

  const totals = useMemo(() => {
    const h = filtered.reduce((s, e) => s + (e.hours || 0), 0);
    const c = filtered.reduce((s, e) => s + (e.hours || 0) * (e.hourly_cost || 0), 0);
    const b = filtered.reduce((s, e) => s + (e.billable && e.hourly_rate ? (e.hours || 0) * e.hourly_rate : 0), 0);
    return { hours: h, cost: c, billable: b };
  }, [filtered]);

  const openNew = () => {
    setEditing({
      project_id: projects[0]?.id || "",
      person_name: "",
      person_id: null,
      date: new Date().toISOString().slice(0, 10),
      hours: 8,
      hourly_cost: 30,
      hourly_rate: 60,
      billable: true,
      description: "",
    });
    setEditOpen(true);
  };

  const openEdit = (e) => { setEditing({ ...e }); setEditOpen(true); };

  const save = async () => {
    if (!editing.project_id || !editing.person_name?.trim() || editing.hours <= 0) {
      toast.error("Compila progetto, persona e ore"); return;
    }
    try {
      if (editing.id) await api.put(`/time-entries/${editing.id}`, editing);
      else await api.post("/time-entries", editing);
      toast.success("Salvato");
      setEditOpen(false); await load();
    } catch (ex) { toast.error(ex?.response?.data?.detail || "Errore"); }
  };
  const remove = async (id) => { if (!window.confirm("Eliminare?")) return; await api.delete(`/time-entries/${id}`); await load(); };

  const applyPerson = (person_id) => {
    const tm = team.find((t) => t.id === person_id);
    if (tm) setEditing({ ...editing, person_id, person_name: tm.name || tm.email, hourly_cost: tm.hourly_cost || editing.hourly_cost, hourly_rate: tm.hourly_rate ?? editing.hourly_rate });
    else setEditing({ ...editing, person_id: null });
  };

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div className="p-8 lg:p-10 space-y-6 fade-up">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="mono-label flex items-center gap-1.5"><Clock className="h-3 w-3" /> Rendicontazione</div>
          <h1 className="font-display text-4xl font-medium text-neutral-900 mt-1">Timesheet</h1>
          <p className="text-sm text-neutral-500 mt-2">Ore su progetto, costi reali, confronto preventivato vs effettivo</p>
        </div>
        <Button onClick={openNew} data-testid="new-te-btn" className="brand-bg hover:opacity-90"><Plus className="h-4 w-4 mr-1.5" /> Nuova riga ore</Button>
      </header>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-0 border border-neutral-200 bg-white" data-testid="te-summary">
        <div className="p-4"><div className="mono-label flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Settimana corrente</div><div className="font-mono text-xl font-semibold mt-1">{weekSummary?.total_hours?.toFixed(1) || 0}h</div><div className="text-xs text-neutral-500 mt-0.5">{fmtEUR(weekSummary?.total_cost || 0)} costo</div></div>
        <div className="p-4 border-l border-neutral-200"><div className="mono-label">Totale filtrato</div><div className="font-mono text-xl font-semibold mt-1">{totals.hours.toFixed(1)}h</div><div className="text-xs text-neutral-500 mt-0.5">{filtered.length} righe</div></div>
        <div className="p-4 border-l border-neutral-200"><div className="mono-label">Costo</div><div className="font-mono text-xl font-semibold mt-1 text-red-700">{fmtEUR(totals.cost)}</div></div>
        <div className="p-4 border-l border-neutral-200"><div className="mono-label">Fatturabile</div><div className="font-mono text-xl font-semibold mt-1 text-emerald-700">{fmtEUR(totals.billable)}</div></div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-3">
        <span className="mono-label">Filtra progetto</span>
        <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} data-testid="te-filter" className="border border-neutral-300 px-3 py-1.5 text-sm bg-white">
          <option value="">Tutti i progetti</option>
          {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
      </div>

      {/* Entries table */}
      <div className="border border-neutral-200 bg-white">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-neutral-500">
            <Clock className="h-8 w-8 mx-auto mb-2 text-neutral-300" />
            Nessuna ora registrata. Inizia a registrare il tempo che dedichi ai progetti per vedere la redditività reale.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr className="text-left">
                <th className="p-3 mono-label">Data</th>
                <th className="p-3 mono-label">Progetto</th>
                <th className="p-3 mono-label">Persona</th>
                <th className="p-3 mono-label">Descrizione</th>
                <th className="p-3 mono-label text-right">Ore</th>
                <th className="p-3 mono-label text-right">Costo</th>
                <th className="p-3 mono-label text-right">Fatt.</th>
                <th className="p-3 mono-label text-right">—</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {filtered.map((e) => (
                <tr key={e.id} className="hover:bg-neutral-50" data-testid={`te-${e.id}`}>
                  <td className="p-3 font-mono text-xs">{fmtDate(e.date)}</td>
                  <td className="p-3"><button onClick={() => openEdit(e)} className="text-left hover:underline">{e.project_title}</button></td>
                  <td className="p-3">{e.person_name}</td>
                  <td className="p-3 text-neutral-600 text-xs truncate max-w-xs">{e.description || "—"}</td>
                  <td className="p-3 text-right font-mono">{e.hours}h</td>
                  <td className="p-3 text-right font-mono text-red-700">{fmtEUR((e.hours || 0) * (e.hourly_cost || 0))}</td>
                  <td className="p-3 text-right font-mono text-emerald-700">{e.billable && e.hourly_rate ? fmtEUR((e.hours || 0) * e.hourly_rate) : "—"}</td>
                  <td className="p-3 text-right"><button onClick={() => remove(e.id)} data-testid={`del-te-${e.id}`} className="p-1.5 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Modifica ore" : "Nuova riga ore"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mono-label">Progetto</label>
                  <select value={editing.project_id} onChange={(e) => setEditing({ ...editing, project_id: e.target.value })} data-testid="te-project" className="w-full border border-neutral-300 px-3 py-2 text-sm">
                    <option value="">— scegli —</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mono-label">Data</label>
                  <Input type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} />
                </div>
              </div>
              <div>
                <label className="mono-label flex items-center gap-1"><Users className="h-3 w-3" /> Persona</label>
                <div className="grid grid-cols-2 gap-2">
                  <select value={editing.person_id || ""} onChange={(e) => applyPerson(e.target.value)} className="border border-neutral-300 px-3 py-2 text-sm">
                    <option value="">— esterno/manuale —</option>
                    {team.map((t) => <option key={t.id} value={t.id}>{t.name || t.email}{t.external ? " (esterno)" : ""}</option>)}
                  </select>
                  <Input value={editing.person_name || ""} onChange={(e) => setEditing({ ...editing, person_name: e.target.value })} placeholder="Nome persona" data-testid="te-person" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="mono-label">Ore</label>
                  <Input type="number" step="0.25" value={editing.hours} onChange={(e) => setEditing({ ...editing, hours: parseFloat(e.target.value) || 0 })} data-testid="te-hours" />
                </div>
                <div>
                  <label className="mono-label">Costo €/h</label>
                  <Input type="number" value={editing.hourly_cost} onChange={(e) => setEditing({ ...editing, hourly_cost: parseFloat(e.target.value) || 0 })} />
                </div>
                <div>
                  <label className="mono-label">Tariffa cliente €/h</label>
                  <Input type="number" value={editing.hourly_rate || 0} onChange={(e) => setEditing({ ...editing, hourly_rate: parseFloat(e.target.value) || 0 })} />
                </div>
              </div>
              <div>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!editing.billable} onChange={(e) => setEditing({ ...editing, billable: e.target.checked })} />
                  Fatturabile al cliente
                </label>
              </div>
              <div>
                <label className="mono-label">Descrizione</label>
                <Textarea rows={2} value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="te-save" className="brand-bg hover:opacity-90">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
